// ============================================================================
// FacilityFeedbackList — CW-I Phase 2.
//
// THE DATA WAS NEVER MISSING. THE LIST STOPPED RENDERING.
//
// Catalyzing case: Payton (EID-0035) logged FBK-0254 on Des Peres Square
// Surgery Center (FAC-0103) on 2026-08-05 and could not find it afterwards.
// Five feedback rows exist on that facility, RLS permits the read
// (tcs_employee_read_all -> is_app_employee()) and `authenticated` holds
// SELECT. facility-detail.tsx fetched all five rows and used them to compute
// exactly two numbers — an open count and a last-feedback date — and then
// rendered none of them. The Activity tab was refactored at some point and
// lost `facility_feedback` as a source; the header comment claiming
// "live: feedback only" has been false ever since.
//
// TWO DESIGN RULES, both learned from that failure:
//
//   1. DEFAULT TO ALL STATUSES. At FAC-0103 four of five rows are Closed. An
//      Open-only default would still have shown Payton almost nothing, and we
//      would have "fixed" this into the same symptom. Open-only is a toggle,
//      never the default.
//
//   2. QUERY facility_feedback DIRECTLY. Do NOT route this through
//      v_facility_event_timeline — that view is security_invoker=false
//      (verified 2026-08-07), so it bypasses RLS on its base tables. Flagged
//      for the view-security backlog; not built on here.
//
// Reporter names resolve through v_employee_display, never a raw join to
// `employees` (2026-08-03 repoint — employees RLS blocks AM/sales/anon, and
// v_employees_am returns zero rows when auth.uid() is null).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { supabase } from '../../../shared/supabase';
import { EmptyState, StatusPill } from '../../../shared/components';
import { fmtDate } from '../../../shared/format';
import type { FacilityFeedback } from '../../../shared/types';

const OPEN_STATUSES = new Set(['Open', 'In_Progress', 'Recurring']);

export function isOpenFeedback(f: Pick<FacilityFeedback, 'status'>): boolean {
  return OPEN_STATUSES.has(f.status);
}

function StatusChip({ status }: { status: string }) {
  // Deliberately NOT green/amber/red: those are reserved for RAG across the
  // whole UI. Open vs Closed reads as weight + border, not colour.
  const open = OPEN_STATUSES.has(status);
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px]',
        open
          ? 'border-tcs-border-strong bg-tcs-surface font-semibold text-tcs-text'
          : 'border-tcs-border bg-tcs-bg text-tcs-text-muted',
      ].join(' ')}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function FacilityFeedbackList({
  feedback,
}: {
  feedback: FacilityFeedback[];
}) {
  const [openOnly, setOpenOnly] = useState(false);
  const [names, setNames] = useState<Map<string, string>>(new Map());

  // Resolve reporter display names once per distinct EID on the page.
  const reporterEids = useMemo(
    () =>
      [...new Set(feedback.map((f) => f.reported_by_employee_id).filter((x): x is string => !!x))],
    [feedback],
  );

  useEffect(() => {
    if (reporterEids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('v_employee_display')
        .select('employee_id, employee_full_name')
        .in('employee_id', reporterEids);
      if (cancelled || error) return;
      setNames(
        new Map(
          (data ?? []).map((r) => [
            r.employee_id as string,
            (r.employee_full_name as string | null) ?? '',
          ]),
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [reporterEids]);

  const rows = useMemo(
    () =>
      (openOnly ? feedback.filter(isOpenFeedback) : feedback)
        .slice()
        .sort((a, b) => (a.feedback_date < b.feedback_date ? 1 : -1)),
    [feedback, openOnly],
  );

  const openCount = feedback.filter(isOpenFeedback).length;

  if (feedback.length === 0) {
    return (
      <div className="p-8">
        <EmptyState
          title="No feedback logged yet"
          body="Feedback you or another AM log for this facility will appear here — open and closed."
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-tcs-text-muted">
          {feedback.length} total · {openCount} open
        </div>
        {/* Open-only is a TOGGLE, never the default — see the header note. */}
        <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-xs text-tcs-text">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
            className="h-4 w-4 rounded border-tcs-border-strong"
          />
          Open only
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="p-8">
          <EmptyState
            title="Nothing open right now"
            body={`All ${feedback.length} feedback item${feedback.length === 1 ? '' : 's'} on this facility ${feedback.length === 1 ? 'is' : 'are'} closed. Untick "Open only" to see them.`}
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((f) => {
            const reporter = f.reported_by_employee_id
              ? names.get(f.reported_by_employee_id) || null
              : null;
            return (
              <li
                key={f.feedback_id}
                className="rounded-lg border border-tcs-border bg-tcs-bg p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill variant="sentiment" value={f.sentiment} />
                  <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] text-tcs-text-secondary">
                    {f.category.replace(/_/g, ' ')}
                  </span>
                  <StatusChip status={f.status} />
                  <span className="ml-auto text-xs text-tcs-text-muted">
                    {fmtDate(f.feedback_date)}
                  </span>
                </div>

                <p className="mt-2 whitespace-pre-wrap text-sm text-tcs-text">
                  {f.description}
                </p>

                {f.resolution && (
                  <div className="mt-2 rounded-md border border-tcs-border bg-tcs-bg-muted p-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                      Resolution
                      {f.resolution_date ? ` · ${fmtDate(f.resolution_date)}` : ''}
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-tcs-text-secondary">
                      {f.resolution}
                    </p>
                  </div>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-tcs-text-muted">
                  <MessageSquare className="h-3 w-3" />
                  <span className="font-mono">{f.feedback_id}</span>
                  {reporter && <span>· logged by {reporter}</span>}
                  {f.source && <span>· {f.source.replace(/_/g, ' ')}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
